import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Button,
  Label,
  Input,
  useToast,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  SearchableSelect,
} from "@racoon/ui";
import { useT, Namespace, type TranslationKey } from "@racoon/i18n";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { ConnectProto } from "@racoon/api";
import { isShiftTerminal } from "~/shared/lib/shift-status";
import {
  useCreateTruckMutation,
  useGetTruckByIdQuery,
  useUpdateTruckMutation,
} from "~/shared/api/truck/truck.grpc";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDriverOnShiftExistsQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { useListTruckTypesQuery } from "~/shared/api/truck/truck-type.api";
import { useGetTruckLatestReadingsQuery } from "~/shared/api/analytics/analytics.grpc";
import { IcJourney, IcPetrol, IcPlus } from "@racoon/icons";
import { ArrowRight } from "lucide-react";
import { TruckImage } from "~/shared/ui/truck-image";
import { TruckCapabilitiesModal } from "~/features/truck-capabilities/ui/truck-capabilities.modal";
import { foreignOwnerName } from "~/shared/lib/holding-type";

/**
 * One tracker reading: label on the left, the figure in a disabled field on the
 * right. Read-only because nothing here is the dispatcher's to set — a missing
 * reading shows an empty field rather than a 0 the tracker never sent.
 */
const ReadOnlyReading = ({
  icon,
  label,
  value,
  unit,
}: {
  icon: React.ReactNode;
  label: string;
  value?: number;
  unit: string;
}) => (
  <div className="flex items-center justify-between gap-2">
    <span className="flex items-center gap-2 text-sm">
      {icon}
      {label}
    </span>
    <span className="flex h-8 min-w-[140px] items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-100 px-2 text-sm">
      <span className="text-gray-500">{value == null ? "" : Math.round(value)}</span>
      <span className="text-gray-400">{unit}</span>
    </span>
  </div>
);

// Form state uses custom types for convenience; maps to proto Truck/TruckCreate on API calls
interface TruckFormData {
  truck_type_id: number | undefined;
  plate_number: string;
  year: number | undefined;
  color: string;
  technical_passport: string;
  capacity: number | undefined;
}

interface TruckModalProps {
  truck?: ConnectProto.Truck;
  /** The truck's shifts for the open day; archiving is blocked while one is still open. */
  shifts?: ConnectProto.DriverOnShift[];
  onTruckSaved?: () => void;
  trigger?: React.ReactNode;
}

export const TruckModal: React.FC<TruckModalProps> = ({ truck, shifts, onTruckSaved, trigger }) => {
  const t = useT();
  const { activeCompanyId } = useAuthSession();
  const selectedCompanyId = activeCompanyId ?? undefined;
  const { toast } = useToast();

  // Fetch truck types
  const { data: truckTypesResponse, isLoading: isLoadingTruckTypes } = useListTruckTypesQuery(
    selectedCompanyId ? { ownerOrganizationIds: [BigInt(selectedCompanyId)] } : skipToken,
  );
  const truckTypes = useMemo(
    () => truckTypesResponse?.customTruckTypes ?? [],
    [truckTypesResponse],
  );

  // Modal state
  const [isOpen, setIsOpen] = useState(false);
  const [, setActiveTab] = useState("info");

  // Fetch full truck details (with embedded truckType) when editing — the truck
  // passed from the list endpoint is shallow and missing nested fields.
  const { data: fullTruck } = useGetTruckByIdQuery(
    truck && isOpen && selectedCompanyId
      ? { id: BigInt(truck.id), ownerOrganizationId: BigInt(selectedCompanyId) }
      : skipToken,
  );
  const editingTruck = fullTruck ?? truck;

  // Telemetry is read-only here: it comes from the tracker, not from this form.
  const { data: readingsData } = useGetTruckLatestReadingsQuery(
    truck && isOpen && selectedCompanyId
      ? { ownerOrganizationId: BigInt(selectedCompanyId), truckIds: [BigInt(truck.id)] }
      : skipToken,
  );
  const readings = readingsData?.rows?.[0];

  // API mutations
  const [createTruck, { isLoading: isCreating }] = useCreateTruckMutation();
  const [updateTruck, { isLoading: isUpdating }] = useUpdateTruckMutation();

  const isLoading = isCreating || isUpdating;

  // Initial form state
  const initialFormData: TruckFormData = useMemo(
    () => ({
      truck_type_id: undefined,
      plate_number: "",
      year: undefined,
      color: "",
      technical_passport: "",
      capacity: undefined,
    }),
    [],
  );

  // Form state
  const [formData, setFormData] = useState<TruckFormData>(initialFormData);

  // Truck type options for the searchable select (bigint ids → string)
  const truckTypeOptions = useMemo(
    // Another company of the holding made it: say whose
    () =>
      truckTypes.map((tt) => ({
        id: String(tt.id),
        name: tt.name || "",
        secondary: foreignOwnerName(tt, selectedCompanyId),
      })),
    [truckTypes, selectedCompanyId],
  );

  // Get selected truck type - first check from API, then from embedded truck_type object
  const selectedTruckType = useMemo(() => {
    const fromApi = truckTypes.find((t) => Number(t.id) === formData.truck_type_id);
    if (fromApi) return fromApi;

    // Fallback to embedded truckType if available (populated during edit mode)
    if (
      formData.truck_type_id &&
      editingTruck?.truckType &&
      Number(editingTruck.truckType.id) === formData.truck_type_id
    ) {
      return editingTruck.truckType;
    }

    return undefined;
  }, [formData.truck_type_id, truckTypes, editingTruck]);

  // Built from the form rather than the saved truck, so the preview follows the
  // type, year and colour as they are edited.
  const previewTruck = useMemo(
    () => ({
      ...editingTruck,
      truckType: selectedTruckType,
      year: formData.year,
      color: formData.color,
    }),
    [editingTruck, selectedTruckType, formData.year, formData.color],
  );
  const [isCapabilitiesOpen, setIsCapabilitiesOpen] = useState(false);

  // Map TruckStandard enum to readable text using i18n
  const getTruckStandardText = (standard?: number): string => {
    if (!standard || standard === 0) return "-";
    const enumName = ConnectProto.TruckStandard[standard];
    if (!enumName) return "-";
    return t(Namespace.Enum, `TRUCK_STANDARD_${enumName}` as TranslationKey<Namespace.Enum>);
  };

  // Map TruckType enum to readable text using i18n
  const getTruckTypeText = (type?: number): string => {
    if (!type || type === 0) return "-";
    const enumName = ConnectProto.TruckType[type];
    if (!enumName) return "-";
    return t(Namespace.Enum, `TRUCK_TYPE_${enumName}` as TranslationKey<Namespace.Enum>);
  };

  // Initialize form when editing
  useEffect(() => {
    if (editingTruck && isOpen) {
      const truckTypeId = editingTruck.truckType?.id;

      setFormData({
        truck_type_id: truckTypeId ? Number(truckTypeId) : undefined,
        plate_number: editingTruck.plateNumber,
        year: editingTruck.year ? Number(editingTruck.year) : undefined,
        color: editingTruck.color || "",
        technical_passport: editingTruck.techPassport || "",
        capacity: editingTruck.capacity ? Number(editingTruck.capacity) : undefined,
      });
    } else if (!truck && isOpen) {
      setFormData(initialFormData);
    }
  }, [editingTruck, truck, isOpen, initialFormData]);

  // Field change handlers
  const handleInputChange = useCallback(
    (field: keyof TruckFormData, value: string | number | undefined) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  // Save handler
  const handleSave = useCallback(async () => {
    if (!formData.plate_number.trim()) {
      console.error("Гос. номер обязателен");
      return;
    }

    if (!formData.truck_type_id) {
      console.error("Тип машины обязателен");
      return;
    }

    if (!selectedCompanyId) {
      console.error("Не выбрана организация");
      return;
    }

    try {
      if (truck) {
        await updateTruck({
          id: BigInt(truck.id),
          ownerOrganizationId: BigInt(selectedCompanyId),
          truckTypeId: BigInt(formData.truck_type_id),
          plateNumber: formData.plate_number,
          year: Number(formData.year || 0),
          color: formData.color || "",
          techPassport: formData.technical_passport || "",
          capacity: BigInt(formData.capacity || 0),
        }).unwrap();
      } else {
        await createTruck({
          truckTypeId: BigInt(formData.truck_type_id),
          plateNumber: formData.plate_number,
          year: Number(formData.year || 0),
          color: formData.color || "",
          techPassport: formData.technical_passport || "",
          capacity: BigInt(formData.capacity || 0),
          ownerOrganizationId: BigInt(selectedCompanyId),
        }).unwrap();
      }

      setIsOpen(false);
      toast({
        title: truck
          ? t(Namespace.Autopark, "notifications.truckUpdated")
          : t(Namespace.Autopark, "notifications.truckCreated"),
        description: truck
          ? t(Namespace.Autopark, "notifications.truckUpdatedDescription", {
              plateNumber: formData.plate_number,
            })
          : t(Namespace.Autopark, "notifications.truckCreatedDescription", {
              plateNumber: formData.plate_number,
            }),
      });

      onTruckSaved?.();
    } catch (error) {
      console.error("Ошибка при сохранении машины:", error);
      toast({
        title: t(Namespace.Autopark, "notifications.truckError"),
        description: truck
          ? t(Namespace.Autopark, "notifications.truckUpdateError")
          : t(Namespace.Autopark, "notifications.truckCreateError"),
        variant: "destructive",
      });
    }
  }, [formData, selectedCompanyId, truck, updateTruck, createTruck, toast, onTruckSaved, t]);

  // Archive doubles as TruckStatus.INACTIVE (no separate status in the contract).
  // Partial update: the backend skips unset fields, so only the status is sent.
  const isArchived = editingTruck?.status === ConnectProto.TruckStatus.INACTIVE;

  // Date-independent: `shifts` is whatever day the card is showing, so on its own
  // it would miss a shift running on another day and let the truck be archived.
  const { data: openShifts, isSuccess: openShiftsKnown } = useDriverOnShiftExistsQuery(
    truck && isOpen && selectedCompanyId
      ? {
          truckId: BigInt(truck.id),
          // No driver to match on; the backend ORs the two and id 0 matches nothing.
          driverId: 0n,
          ownerOrganizationIds: [BigInt(selectedCompanyId)],
        }
      : skipToken,
  );

  // The RPC covers running shifts on any day; the day's own list adds the planned
  // ones, which it does not report.
  const hasOpenShift =
    (openShifts?.driverOnShifts?.length ?? 0) > 0 ||
    (shifts ?? []).some((shift) => !isShiftTerminal(shift.status));

  // Archiving drops the truck out of the registry's ACTIVE-only fleet query, which
  // is where the dispatcher changes a shift's driver — so a still-open shift would
  // be left running with no way to reassign it. Close the shift first.
  //
  // Fails closed: until the lookup answers — or if it fails — the truck is treated
  // as possibly occupied, rather than letting an archive through on a blank answer.
  const blockedByOpenShift = !isArchived && (!openShiftsKnown || hasOpenShift);

  const handleArchiveToggle = useCallback(async () => {
    if (!truck || !selectedCompanyId) return;

    try {
      await updateTruck({
        id: BigInt(truck.id),
        ownerOrganizationId: BigInt(selectedCompanyId),
        status: isArchived ? ConnectProto.TruckStatus.ACTIVE : ConnectProto.TruckStatus.INACTIVE,
      }).unwrap();
      setIsOpen(false);
      toast({
        title: isArchived
          ? t(Namespace.Autopark, "notifications.truckUnarchived")
          : t(Namespace.Autopark, "notifications.truckArchived"),
        description: t(Namespace.Autopark, "notifications.truckUpdatedDescription", {
          plateNumber: truck.plateNumber,
        }),
      });
      onTruckSaved?.();
    } catch (error) {
      console.error("Ошибка при архивировании машины:", error);
      toast({
        title: t(Namespace.Autopark, "notifications.truckError"),
        description: t(Namespace.Autopark, "notifications.truckUpdateError"),
        variant: "destructive",
      });
    }
  }, [truck, selectedCompanyId, isArchived, updateTruck, toast, onTruckSaved, t]);

  const isEditMode = !!truck;
  const modalTitle = isEditMode
    ? t(Namespace.Autopark, "modals.editTruck.title")
    : t(Namespace.Autopark, "modals.addTruck.title");

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          {trigger || (
            <Button
              id="truck-modal-trigger-button"
              size="sm"
              className="gap-2"
              variant="opacityBlueWithoutOutline"
            >
              <IcPlus className="w-5 h-5" />
              {t(Namespace.Autopark, "buttons.addTruck")}
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[50rem] w-[43.75rem] !p-0 overflow-auto">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle>{modalTitle}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="info" className="w-full" onValueChange={setActiveTab}>
            <div className="px-6 pt-2">
              <TabsList className="w-full gap-1">
                <TabsTrigger value="info" className="flex-1 ">
                  {t(Namespace.Autopark, "modals.truckModal.infoTab")}
                </TabsTrigger>
                <TabsTrigger value="sensors" className="flex-1 ">
                  {t(Namespace.Autopark, "modals.truckModal.sensorsTab")}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6">
              <TabsContent value="info" className="mt-0 space-y-6 data-[state=inactive]:hidden">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Left Column: Form Fields */}
                  <div className="flex-1 space-y-4">
                    {/* Truck type */}
                    <div className="flex items-center justify-between w-full">
                      <Label
                        htmlFor="truck-type-select"
                        className="text-sm flex flex-1 font-medium min-w-fit"
                      >
                        {t(Namespace.Common, "type")}
                      </Label>
                      <SearchableSelect
                        id="truck-type-select"
                        value={
                          formData.truck_type_id !== undefined ? String(formData.truck_type_id) : ""
                        }
                        options={truckTypeOptions}
                        onChange={(id) => handleInputChange("truck_type_id", Number(id))}
                        loading={isLoadingTruckTypes}
                        // Embedded truckType name shown as the value while the list is
                        // still missing it (edit mode).
                        valueLabel={selectedTruckType?.name}
                        placeholder={t(Namespace.Autopark, "modals.addTruck.type.placeholder")}
                        searchPlaceholder={t(
                          Namespace.Autopark,
                          "modals.addTruck.type.placeholder",
                        )}
                        size="sm"
                        triggerExtraClassName="flex-1"
                      />
                    </div>

                    {/* Make (Read-only) */}
                    <div className="flex items-center justify-between w-full">
                      <Label className="text-sm flex flex-1 font-medium min-w-fit">
                        {t(Namespace.Autopark, "modals.addTruck.model.label")}
                      </Label>
                      <div className="flex flex-1 h-[32px] px-2 py-1 bg-gray-100 rounded-lg border border-gray-200 items-center">
                        <p className="text-sm text-gray-500">
                          {selectedTruckType?.manufacturer || "-"}
                        </p>
                      </div>
                    </div>

                    {/* Model (Read-only) */}
                    <div className="flex items-center justify-between w-full">
                      <Label className="text-sm flex flex-1 font-medium min-w-fit">
                        {t(Namespace.Autopark, "modals.truckModal.model.label")}
                      </Label>
                      <div className="flex flex-1 h-[32px] px-2 py-1 bg-gray-100 rounded-lg border border-gray-200 items-center">
                        <p className="text-sm text-gray-500">{selectedTruckType?.model || "-"}</p>
                      </div>
                    </div>

                    {/* Vehicle weight (Read-only) */}
                    <div className="flex items-center justify-between w-full">
                      <Label className="text-sm flex flex-1 font-medium min-w-fit">
                        {t(Namespace.Autopark, "modals.truckModal.vehicleWeight.label")}
                      </Label>
                      <div className="flex flex-1 h-[32px] px-2 py-1 bg-gray-100 rounded-lg border border-gray-200 items-center">
                        <p className="text-sm text-gray-500">
                          {selectedTruckType?.weightKg ? `${selectedTruckType.weightKg} кг` : "-"}
                        </p>
                      </div>
                    </div>

                    {/* Standard (Read-only) */}
                    <div className="flex items-center justify-between w-full">
                      <Label className="text-sm flex flex-1 font-medium min-w-fit">
                        {t(Namespace.Autopark, "modals.truckModal.standard.label")}
                      </Label>
                      <div className="flex flex-1 h-[32px] px-2 py-1 bg-gray-100 rounded-lg border border-gray-200 items-center">
                        <p className="text-sm text-gray-500">
                          {getTruckStandardText(selectedTruckType?.euroType)}
                        </p>
                      </div>
                    </div>

                    {/* Vehicle category (Read-only) */}
                    <div className="flex items-center justify-between w-full">
                      <Label className="text-sm flex flex-1 font-medium min-w-fit">
                        {t(Namespace.Autopark, "modals.truckModal.vehicleCategory.label")}
                      </Label>
                      <div className="flex flex-1 h-[32px] px-2 py-1 bg-gray-100 rounded-lg border border-gray-200 items-center">
                        <p className="text-sm text-gray-500">
                          {getTruckTypeText(selectedTruckType?.type)}
                        </p>
                      </div>
                    </div>

                    {/* License plate */}
                    <div className="flex items-center justify-between w-full">
                      <Label htmlFor="plate_number" className="text-sm font-medium flex flex-1">
                        {t(Namespace.Autopark, "modals.truckModal.plateNumber.label")}
                      </Label>
                      <Input
                        id="plate_number"
                        value={formData.plate_number}
                        onChange={(e) => handleInputChange("plate_number", e.target.value)}
                        placeholder={t(
                          Namespace.Autopark,
                          "modals.addTruck.plateNumber.placeholder",
                        )}
                        className="flex flex-1 h-[32px]"
                      />
                    </div>

                    {/* Trailer (stored in the truck's legacy tech_passport field) */}
                    <div className="flex items-center justify-between w-full">
                      <Label
                        htmlFor="technical_passport"
                        className="text-sm font-medium flex flex-1"
                      >
                        {t(Namespace.Autopark, "modals.truckModal.techPassport.label")}
                      </Label>
                      <Input
                        id="technical_passport"
                        value={formData.technical_passport}
                        onChange={(e) => handleInputChange("technical_passport", e.target.value)}
                        placeholder={t(
                          Namespace.Autopark,
                          "modals.truckModal.techPassport.placeholder",
                        )}
                        className="flex flex-1 h-[32px]"
                      />
                    </div>

                    {/* Year of manufacture */}
                    <div className="flex items-center justify-between w-full">
                      <Label htmlFor="year" className="text-sm font-medium flex flex-1">
                        {t(Namespace.Autopark, "modals.truckModal.year.label")}
                      </Label>
                      <Input
                        id="year"
                        type="number"
                        value={formData.year || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "year",
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                        placeholder={t(Namespace.Autopark, "modals.truckModal.year.placeholder")}
                        className="flex flex-1 h-[32px]"
                      />
                    </div>

                    {/* Color */}
                    <div className="flex items-center justify-between w-full">
                      <Label htmlFor="color" className="text-sm font-medium flex flex-1">
                        {t(Namespace.Autopark, "modals.truckModal.color.label")}
                      </Label>
                      <Input
                        id="color"
                        value={formData.color}
                        onChange={(e) => handleInputChange("color", e.target.value)}
                        placeholder={t(Namespace.Autopark, "modals.truckModal.color.placeholder")}
                        className="flex flex-1 h-[32px]"
                      />
                    </div>
                  </div>

                  {/* Right Column: Image & Photo Generation */}
                  <div className="w-full md:w-[300px] flex flex-col gap-4">
                    {/* TODO: to be deprecated. The S3 render is keyed on a year that
                        defaults to "last year" and on a free-text colour, so it stops
                        resolving and 404s; TruckImage's bundled fallback hides that the
                        way the autopark card does, until the naming is fixed. */}
                    <div className="aspect-[4/3] w-full rounded-lg border bg-muted/20 flex items-center justify-center overflow-hidden p-4">
                      <TruckImage truck={previewTruck} />
                    </div>

                    {editingTruck && (
                      <>
                        <div className="flex flex-col gap-3 rounded-lg border p-3">
                          <ReadOnlyReading
                            icon={<IcJourney className="h-5 w-5 shrink-0 text-brand-blue" />}
                            label={t(Namespace.Autopark, "modals.truckModal.mileage.label")}
                            value={readings?.mileageKm}
                            unit={t(Namespace.Autopark, "units.km")}
                          />
                          <ReadOnlyReading
                            icon={<IcPetrol className="h-5 w-5 shrink-0 text-brand-blue" />}
                            label={t(Namespace.Autopark, "modals.truckModal.fuelLevel.label")}
                            value={readings?.fuelLitres}
                            unit={t(Namespace.Autopark, "units.liters")}
                          />
                        </div>

                        <Button
                          id="truck-modal-capabilities-button"
                          type="button"
                          variant="opacityBlueWithoutOutline"
                          size="sm"
                          className="ml-auto gap-2"
                          onClick={() => setIsCapabilitiesOpen(true)}
                        >
                          {t(Namespace.PickupLocation, "groups.truckCapabilities")}
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent
                value="sensors"
                className="mt-0 h-[400px] flex items-center justify-center border rounded-lg bg-brand-blue-light/5 data-[state=inactive]:hidden"
              >
                <div className="text-center text-muted-foreground">
                  <p>{t(Namespace.Autopark, "modals.truckModal.noSensors")}</p>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <div className="flex border-t justify-between gap-2 px-6 py-4 bg-white">
            {truck && (
              <Button
                id="truck-modal-archive-button"
                variant="solidRed"
                className="w-full sm:w-auto min-w-[120px]"
                onClick={handleArchiveToggle}
                disabled={isLoading || blockedByOpenShift}
                title={
                  hasOpenShift
                    ? t(Namespace.Autopark, "modals.truckModal.archiveBlocked")
                    : undefined
                }
              >
                {isArchived
                  ? t(Namespace.Autopark, "buttons.unarchive")
                  : t(Namespace.Autopark, "buttons.archive")}
              </Button>
            )}
            <Button
              id="truck-modal-save-button"
              variant="solidBlue"
              className="w-full sm:w-auto min-w-[120px] ml-auto"
              onClick={handleSave}
              disabled={!formData.plate_number.trim() || !formData.truck_type_id || isLoading}
            >
              {isLoading ? t(Namespace.Common, "saving") : t(Namespace.Common, "save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {editingTruck && (
        <TruckCapabilitiesModal
          open={isCapabilitiesOpen}
          onOpenChange={setIsCapabilitiesOpen}
          truck={editingTruck}
        />
      )}
    </>
  );
};
